/**
 * Pure helpers for CallCenterHome shell (menu shaping, tab titles, banner color).
 *
 * Activation / cutover (PortalHome stays on CallCenterHome5b until verified):
 * 1. Publish host.UserControl.json as public UserControl `v1/Vue/CallCenterHomeNative`
 * 2. Either set appSettings.PortalHome to `public/v1/Vue/CallCenterHomeNative`,
 *    or set localStorage key `callcenter.home` to that path (CallCenterPortal already honors it).
 */

/**
 * Build left-rail menu model from CareCenterMenu docs.
 * Filters out OpenTabs placeholders (dynamic tabs use the title-bar strip instead).
 *
 * @param {object} menus - utils.document_cache.menus
 * @param {(key: string) => string} translate
 * @returns {Array<object>}
 */
export function buildCareCenterMenu(menus, translate) {
    const careCenter = menus?.CareCenterMenu;
    if (!Array.isArray(careCenter))
        return [];

    const translateFn = typeof translate === 'function' ? translate : (k) => k;

    return careCenter
        .map((m) => {
            const mid = m.MenuItemData || {};
            const childType = mid.ChildMenuType;
            return {
                index: parseInt(m.Index, 10) + (mid.DisplayLocation === 'Bottom' ? 100000 : 0),
                location: mid.DisplayLocation || 'Top',
                kind: m.MenuItemType,
                type: childType,
                Icon: mid.Icon,
                Name: mid.Name,
                Title: mid.Title,
                Data: m,
                Actions: mid.Actions,
                ChildData: childType === 'PopupMenu' && mid.PopupMenuName && Array.isArray(menus[mid.PopupMenuName])
                    ? menus[mid.PopupMenuName]
                        .map((c) => ({
                            index: parseInt(c.Index, 10),
                            Title: translateFn(c.MenuItemData?.Title),
                            Icon: c.MenuItemData?.Icon,
                            Divider: c.MenuItemData?.ItemClass === 'dividerItem',
                            Data: c,
                            _navChild: c,
                        }))
                        .sort((a, b) => a.index - b.index)
                    : null,
            };
        })
        .filter((row) => row.type !== 'OpenTabs')
        .sort((a, b) => a.index - b.index);
}

/**
 * @param {{ DEBUG?: boolean, Environment?: string }} system
 * @returns {string} CSS color value
 */
export function getBannerColor(system) {
    //if (system?.DEBUG) {
    //    if (system.Environment === 'Development')
    //        return 'var(--v-lightGrey-base)';
    //    if (system.Environment === 'Staging')
    //        return 'var(--v-primaryActive-base)';
    //}
    return 'var(--v-header-base)';
}

/**
 * @param {string} tabTitle
 * @returns {{ Title: string, Tooltip: string|null }}
 */
export function parseTabTitle(tabTitle) {
    if (!tabTitle)
        return { Title: 'Untitled', Tooltip: null };
    const parts = String(tabTitle).split('/');
    return {
        Title: parts.length > 0 ? parts[parts.length - 1] : 'Untitled',
        Tooltip: parts.length > 1 ? tabTitle : null,
    };
}

/**
 * @param {object} payload
 * @param {() => string} generateUUID
 * @returns {object}
 */
export function normalizeOpenTab(payload, generateUUID) {
    const data = payload || {};
    const { Title, Tooltip } = parseTabTitle(data.TabTitle);
    return {
        Title,
        Tooltip,
        ID: data.TabID || generateUUID(),
        ActionData: data.ActionData,
        ControlData: data.ControlData,
        OnActivateActions: data.OnActivateActions,
        OnDeactivateActions: data.OnDeactivateActions,
        context: data.context,
    };
}

/**
 * Layout-shaped host ControlData for a shared panel UserControl document.
 * Wraps the full Data node (ControlType / PrimitiveControl / ControlData) so
 * VerticalStack can mount non-layout roots (e.g. SandboxedApp), matching
 * Control Groups. Cached on the doc so DynamicControlContainer keeps a stable $objectId.
 *
 * @param {object|null} doc - UserControl document from document_cache.sharedpanels
 * @returns {{ Controls: object[], $objectId: string }|null}
 */
export function ensureSharedPanelHostControlData(doc) {
    if (!doc?.Data)
        return null;

    const cached = doc.$cchSharedHost;
    if (cached && cached.Controls?.[0] === doc.Data)
        return cached;

    const host = {
        Controls: [doc.Data],
        $objectId: cached?.$objectId || (
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `sharedHost_${Date.now()}`
        ),
    };
    doc.$cchSharedHost = host;
    return host;
}

/**
 * Build a DynamicMenuList-shaped tree from a flat CareCenter menu array (MenuPath / Index).
 * Same structure as DynamicMenuList.getMenuTree — used by careAgent when the list is not mounted yet.
 *
 * @param {Array<object>|null|undefined} data
 * @returns {Array<{ id: string, name: string, icon?: string, item_data: object, children?: object[] }>}
 */
export function buildDynamicMenuTree(data) {
    if (!Array.isArray(data) || data.length === 0)
        return [];

    const content = [];
    const outargs = { index: 0, content };

    function getChildren(parent) {
        let path = parent.MenuItemData?.Title;
        if (parent.MenuPath)
            path = parent.MenuPath + '::' + path;

        const list = data
            .filter((m) => m.MenuPath == path)
            .sort((a, b) => parseInt(a.Index, 10) - parseInt(b.Index, 10));

        const result = [];
        for (let i = 0; i < list.length; i++) {
            const item = list[i];
            const child = {
                id: `${outargs.index++}`,
                name: item.MenuItemData?.Title,
                icon: item.MenuItemData?.Icon,
                item_data: item,
            };
            outargs.content.push(child);
            const children = getChildren(item);
            if (children.length > 0)
                child.children = children;
            result.push(child);
        }
        return result;
    }

    const result = [];
    const root = data
        .filter((m) => !m.MenuPath)
        .sort((a, b) => parseInt(a.Index, 10) - parseInt(b.Index, 10));

    for (let i = 0; i < root.length; i++) {
        const node = root[i];
        const child = {
            id: `${outargs.index++}`,
            name: node.MenuItemData?.Title,
            icon: node.MenuItemData?.Icon,
            item_data: node,
        };
        outargs.content.push(child);
        const children = getChildren(node);
        if (children.length > 0)
            child.children = children;
        result.push(child);
    }
    return result;
}

/**
 * Match a menu segment by Title first, then Name (translated titles vs stable ids).
 * @param {{ Title?: string, Name?: string, title?: string, name?: string }|null|undefined} mid
 * @param {string} part
 */
export function menuSegmentMatches(mid, part) {
    if (!part)
        return false;
    if (!mid)
        return false;
    return mid.Title === part
        || mid.Name === part
        || mid.title === part
        || mid.name === part;
}

/**
 * Resolve active menu content kind for a selected DynamicMenuList item.
 * @param {object|null} menuItem - MenuContent entry ({ id, item_data, ... })
 * @param {object} sharedpanels - utils.document_cache.sharedpanels
 * @returns {{ kind: 'item'|'shared'|'missing', key: string, controlData?: object, message?: string }|null}
 */
export function resolveMenuContent(menuItem, sharedpanels) {
    if (!menuItem?.item_data?.MenuItemData)
        return null;

    const mid = menuItem.item_data.MenuItemData;
    const sharedName = mid.SharedPanel;
    if (sharedName) {
        const doc = sharedpanels?.[sharedName];
        if (!doc)
            return { kind: 'missing', key: sharedName, message: `Shared panel "${sharedName}" was not found.` };
        return {
            kind: 'shared',
            key: sharedName,
            controlData: ensureSharedPanelHostControlData(doc),
        };
    }

    return {
        kind: 'item',
        key: String(menuItem.id),
        controlData: mid.ControlData,
    };
}
